import { apiFetch } from "./client.ts";

// ---- Organizations ----

export async function createOrg(token: string | null, data: { name: string; slug?: string; ownerId: string }) {
  return apiFetch("/api/v1/orgs", { token, method: "POST", body: JSON.stringify(data) });
}

export async function getOrg(token: string | null, id: string) {
  return apiFetch(`/api/v1/orgs/${id}`, { token });
}

export async function listOrgMembers(token: string | null, orgId: string) {
  return apiFetch(`/api/v1/orgs/${orgId}/members`, { token });
}

export async function inviteMember(token: string | null, orgId: string, data: { email: string; role: string; invitedBy: string }) {
  return apiFetch(`/api/v1/orgs/${orgId}/invite`, { token, method: "POST", body: JSON.stringify(data) });
}

export async function updateMemberRole(token: string | null, orgId: string, userId: string, role: string) {
  return apiFetch(`/api/v1/orgs/${orgId}/members/${userId}/role`, { token, method: "POST", body: JSON.stringify({ role }) });
}

export async function removeMember(token: string | null, orgId: string, userId: string) {
  return apiFetch(`/api/v1/orgs/${orgId}/members/${userId}`, { token, method: "DELETE" });
}

export async function listInvitations(token: string | null, orgId: string) {
  return apiFetch(`/api/v1/orgs/${orgId}/invitations`, { token });
}

export async function acceptInvitation(authToken: string, invitationToken: string, userId: string) {
  return apiFetch("/api/v1/orgs/invitations/accept", { token: authToken, method: "POST", body: JSON.stringify({ token: invitationToken, userId }) });
}

// ---- API Keys ----

export async function listApiKeys(token: string | null, orgId: string) {
  return apiFetch(`/api/v1/api-keys?orgId=${orgId}`, { token });
}

export async function createApiKey(token: string | null, data: { orgId: string; name: string; createdBy: string; permissions?: string[]; expiresAt?: string }) {
  return apiFetch("/api/v1/api-keys", { token, method: "POST", body: JSON.stringify(data) });
}

export async function revokeApiKey(token: string | null, id: string, userId: string) {
  return apiFetch(`/api/v1/api-keys/${id}?userId=${userId}`, { token, method: "DELETE" });
}

// ---- Webhooks ----

export async function listWebhooks(token: string | null, orgId: string) {
  return apiFetch(`/api/v1/webhooks?orgId=${orgId}`, { token });
}

export async function createWebhook(token: string | null, data: { orgId: string; url: string; events: string[]; createdBy: string }) {
  return apiFetch("/api/v1/webhooks", { token, method: "POST", body: JSON.stringify(data) });
}

export async function getWebhook(token: string | null, id: string) {
  return apiFetch(`/api/v1/webhooks/${id}`, { token });
}

export async function pauseWebhook(token: string | null, id: string) {
  return apiFetch(`/api/v1/webhooks/${id}/pause`, { token, method: "POST", body: JSON.stringify({}) });
}

export async function resumeWebhook(token: string | null, id: string) {
  return apiFetch(`/api/v1/webhooks/${id}/resume`, { token, method: "POST", body: JSON.stringify({}) });
}

export async function listWebhookDeliveries(token: string | null, id: string) {
  return apiFetch(`/api/v1/webhooks/${id}/deliveries`, { token });
}

// ---- Subscriptions ----

export async function getSubscription(token: string | null, orgId: string) {
  return apiFetch(`/api/v1/subscriptions/${orgId}`, { token });
}

export async function changePlan(token: string | null, orgId: string, plan: string) {
  return apiFetch(`/api/v1/subscriptions/${orgId}/plan`, { token, method: "POST", body: JSON.stringify({ plan }) });
}

// ---- Plugins ----

export async function listPlugins(token: string | null, orgId: string) {
  return apiFetch(`/api/v1/plugins?orgId=${orgId}`, { token });
}

export async function registerPlugin(token: string | null, data: {
  orgId: string; name: string; displayName: string;
  version: string; description?: string; permissions?: string[]; createdBy: string;
}) {
  return apiFetch("/api/v1/plugins", { token, method: "POST", body: JSON.stringify(data) });
}

export async function enablePlugin(token: string | null, id: string) {
  return apiFetch(`/api/v1/plugins/${id}/enable`, { token, method: "POST", body: JSON.stringify({}) });
}

export async function disablePlugin(token: string | null, id: string) {
  return apiFetch(`/api/v1/plugins/${id}/disable`, { token, method: "POST", body: JSON.stringify({}) });
}

export async function uninstallPlugin(token: string | null, id: string) {
  return apiFetch(`/api/v1/plugins/${id}`, { token, method: "DELETE" });
}

// ---- Audit Logs ----

export async function listAuditLogs(token: string | null, params?: {
  orgId?: string; userId?: string; action?: string;
  resourceType?: string; since?: string; limit?: number; offset?: number;
}) {
  const q = new URLSearchParams();
  if (params?.orgId) q.set("orgId", params.orgId);
  if (params?.userId) q.set("userId", params.userId);
  if (params?.action) q.set("action", params.action);
  if (params?.resourceType) q.set("resourceType", params.resourceType);
  if (params?.since) q.set("since", params.since);
  if (params?.limit) q.set("limit", String(params.limit));
  if (params?.offset) q.set("offset", String(params.offset));
  return apiFetch(`/api/v1/audit?${q}`, { token });
}
